import type { z } from 'zod/v4';

import type { InferSelectModel } from '@edgevanta/db/drizzle';
import type { schema } from '@edgevanta/db/drizzle/client';
import type { BidAmountRange } from '../../shared/bid-size';
import type {
  getForecastItemsQuerySchema,
  getForecastsCountQuerySchema,
  getForecastsQuerySchema,
  getLandscapeOutputsQuerySchema,
  insertForecastBidAsReadEntrySchema,
  insertForecastCompetitorSchema,
  insertForecastItemSchema,
  insertForecastPostBidEntrySchema,
  insertForecastSchema,
  selectForecastBidAsReadEntrySchema,
  selectForecastCompetitorSchema,
  selectForecastItemSchema,
  selectForecastPostBidEntrySchema,
  selectForecastSchema,
} from './forecasts.schema';

export type SelectForecast = z.infer<typeof selectForecastSchema>;
export type InsertForecast = z.infer<typeof insertForecastSchema>;
export type GetForecastsQuery = z.infer<typeof getForecastsQuerySchema>;
export type GetForecastsCountQuery = z.infer<
  typeof getForecastsCountQuerySchema
>;
export type GetForecastItemsQuery = z.infer<typeof getForecastItemsQuerySchema>;
export type GetLandscapeOutputsQuery = z.infer<
  typeof getLandscapeOutputsQuerySchema
>;
export type Forecast = InferSelectModel<typeof schema.forecasts>;

export type LandscapeProfileEvent = {
  phase: string;
  durationMs?: number;
  rowCount?: number;
  groupCount?: number;
  predictionCount?: number;
  limit?: number;
  offset?: number;
  group?: string | null;
  sortId?: string | null;
  target?: string | null;
  competitorId?: string | null;
  detail?: string;
};

export type LandscapeProfileSink = (event: LandscapeProfileEvent) => void;

export type SelectForecastItem = z.infer<typeof selectForecastItemSchema>;
export type InsertForecastItem = z.infer<typeof insertForecastItemSchema>;
export type ForecastItem = InferSelectModel<typeof schema.forecastItems>;
export type ForecastItemWithCatalogId = ForecastItem & {
  catalogItemId: string | null;
  predictedPrice: string | null;
  bidCount: number | null;
  totalQuantity: string | null;
  fallbackLevel: string | null;
  lumpSumRatio: number | null;
  forecastResultId: string | null;
  lumpSumRatioOverride: number | null;
  isLumpSum: boolean;
  category: string | null;
  matchedItemNumbers: string[] | null;
  matchedDescription: string | null;
  matchedUnit: string | null;
  matchedOwnerId: string | null;
  similarityScore: number | null;
  noDataReason: string | null;
  isLiquidAcAdjustable: boolean;
  isLiquidAcAdjusted: boolean;
};

export type SelectForecastCompetitor = z.infer<
  typeof selectForecastCompetitorSchema
>;
export type InsertForecastCompetitor = z.infer<
  typeof insertForecastCompetitorSchema
>;
export type ForecastCompetitor = InferSelectModel<
  typeof schema.forecastCompetitors
>;
export type ForecastCompetitorWithVendor = ForecastCompetitor & {
  vendor: InferSelectModel<typeof schema.vendors> & {
    globalVendor: InferSelectModel<typeof schema.globalVendors> | null;
  };
};

export type SelectForecastPostBidEntry = z.infer<
  typeof selectForecastPostBidEntrySchema
>;
export type InsertForecastPostBidEntry = z.infer<
  typeof insertForecastPostBidEntrySchema
>;
export type ForecastPostBidEntry = InferSelectModel<
  typeof schema.forecastPostBidEntries
>;

export type SelectForecastBidAsReadEntry = z.infer<
  typeof selectForecastBidAsReadEntrySchema
>;
export type InsertForecastBidAsReadEntry = z.infer<
  typeof insertForecastBidAsReadEntrySchema
>;
export type ForecastBidAsReadEntry = InferSelectModel<
  typeof schema.forecastBidAsReadEntries
>;

export type OfficialBidResult = {
  rank: number | null;
  vendor_name: string | null;
  adjusted_total: string | null;
  deviation: string | null;
  deviation_percent: string | null;
  winner: boolean;
  value: string;
};

// --- Item Types ---

export const ITEM_TYPE_ROUGH_IN = 'rough_in' as const;
export const ITEM_TYPE_PREDICT = 'predict' as const;
export type ForecastItemType =
  | typeof ITEM_TYPE_ROUGH_IN
  | typeof ITEM_TYPE_PREDICT;

// --- Targets ---

export const TARGET_ROUGH_IN = 'rough_in' as const;
export const TARGET_VENDOR = 'vendor' as const;
export const TARGET_MARKET = 'market' as const;

// --- Default Config Filters ---

export const DEFAULT_QTY_LOWER_BOUND = 0.33;
export const DEFAULT_QTY_UPPER_BOUND = 3.0;
export const DEFAULT_LS_BAND_LOWER = 0.25;
export const DEFAULT_LS_BAND_UPPER = 4.0;

const DEFAULT_BANDING: PredictionFilters = {
  qtyLowerBound: DEFAULT_QTY_LOWER_BOUND,
  qtyUpperBound: DEFAULT_QTY_UPPER_BOUND,
  lsBandLower: DEFAULT_LS_BAND_LOWER,
  lsBandUpper: DEFAULT_LS_BAND_UPPER,
};

export function buildDefaultConfigFilters(
  target: string,
  competitorVendorId?: string | null,
): PredictionFilters {
  switch (target) {
    case TARGET_ROUGH_IN:
      // Apply the same default banding as vendor/market so the FIRST
      // post-upload compute already uses a tightened pool. Without this
      // the rough_in config was seeded empty, the upload-triggered
      // computeRoughIn read it as `{}`, and the prediction fell back to
      // the full historical pool (`Statewide Unbanded`) — yielding noisy
      // averages until the user manually clicked "Apply" in the bid-
      // search dialog (which overlays the same defaults). Parity here
      // means uploads predict correctly on day zero.
      return { ...DEFAULT_BANDING };
    case TARGET_MARKET:
      return { ...DEFAULT_BANDING };
    case TARGET_VENDOR:
      return {
        ...DEFAULT_BANDING,
        ...(competitorVendorId ? { vendorIds: [competitorVendorId] } : {}),
      };
    default:
      return {};
  }
}

// --- Landscape Outputs ---

export type LandscapePrediction = {
  target: string;
  competitorId: string | null;
  competitorPosition: number | null;
  competitorName: string | null;
  predictedUnitPrice: string | null;
  /**
   * True when this item's contributing bids include at least one classified,
   * index-moved liquid-AC asphalt line, i.e. `predictedUnitPrice` already
   * reflects a live liquid-AC adjustment. Per-item: identical across every
   * prediction of a row (it does not depend on the forecast config).
   */
  isLiquidAcAdjustable: boolean;
  /**
   * True when the visible predictedUnitPrice actually consumed an applied
   * Liquid AC formula branch. Adjustable rows with missing inputs remain false.
   */
  isLiquidAcAdjusted: boolean;
  unitPriceOverride: string | null;
  forecastResultId: string | null;
  predictedExtension: string | null;
  /**
   * Override-aware extension (qty × COALESCE(override, linked, predicted)).
   * Only populated on synthesized category-group predictions, where the
   * "My Ext" cell needs the effective roll-up while `predictedExtension`
   * carries the raw calculated roll-up. Leaf rows leave this null and
   * compute "My Ext" per-row from the price fields.
   */
  effectiveExtension: string | null;
  fallbackLevel: string | null;
  bidCount: number | null;
  totalQuantity: string | null;
  lumpSumRatio: number | null;
  lumpSumRatioOverride: number | null;
  matchedItemNumbers: string[] | null;
  matchedDescription: string | null;
  matchedUnit: string | null;
  similarityScore: number | null;
  noDataReason: string | null;
};

export type LandscapeOutputRow = {
  forecastItemId: string;
  lineNumber: number;
  itemNumber: string;
  catalogItemId: string | null;
  description: string | null;
  quantity: string;
  unit: string | null;
  unitPrice: string | null;
  actualUnitPrice: string | null;
  isLumpSum: boolean;
  isPriceLinked: boolean;
  category: string | null;
  predictions: LandscapePrediction[];
};

export type LandscapePredictionTotal = {
  target: string;
  competitorId: string | null;
  competitorPosition: number | null;
  predictedExtensionTotal: string;
  effectiveExtensionTotal: string;
  predictedNonLsSubtotal?: string | null;
  predictedLsRatioTotal?: string | null;
  effectiveNonLsSubtotal?: string | null;
  effectiveLsRatioTotal?: string | null;
};

export const SCOPE_MY_PRICES = 'my_prices' as const;
export const SCOPE_SUMMARY = 'summary' as const;

/**
 * Where to apply the rounding. Each variant matches one of the forecast
 * UI surfaces:
 *
 * - `TARGET_ROUGH_IN`  → Rough-In tab. Rounds only
 *                        `forecast_items.unit_price` where type='rough_in'.
 * - `SCOPE_MY_PRICES`  → Landscape → Items tab ("My Current Price").
 *                        Rounds only `forecast_items.unit_price` where
 *                        type='predict'. Not a config target — this is
 *                        the user-editable cost column on predict items.
 * - `TARGET_MARKET`    → Landscape → Market prediction. Rounds the
 *                        `unit_price_override` on results whose config
 *                        target is 'market'.
 * - `TARGET_VENDOR`    → Landscape → Vendor prediction. Same as market
 *                        but target='vendor' AND limited to one
 *                        competitor.
 * - `SCOPE_SUMMARY`    → Landscape → Analysis (forecast summary).
 *                        Rounds predict items + market overrides +
 *                        every vendor override. Rough-in items and
 *                        rough-in configs are NEVER touched.
 */
export type RoundPricesScope =
  | { kind: typeof TARGET_ROUGH_IN }
  | { kind: typeof SCOPE_MY_PRICES }
  | { kind: typeof TARGET_MARKET }
  | { kind: typeof TARGET_VENDOR; competitorId: string }
  | { kind: typeof SCOPE_SUMMARY };

export type LandscapeOutputTotals = {
  unitPriceTotal: string;
  actualUnitPriceTotal: string;
  predictionTotals: LandscapePredictionTotal[];
  hasLandscapeItems: boolean;
};

export type ForecastItemGroupSummary = {
  category: string;
  itemCount: number;
  linkedItemCount: number;
  extensionTotal: string | null;
  predictedExtensionTotal: string | null;
  nonLsSubtotal?: string | null;
  lsRatioTotal?: string | null;
  bidCountTotal: number | null;
  totalQuantityTotal: string | null;
  lumpSumRatioAvg: number | null;
  /**
   * Share of the forecast's extension total attributable to this group,
   * as a 0..1 fraction. Computed server-side from
   * `extensionTotal / SUM(extensionTotal across all groups)`.
   */
  percentOfTotal: number | null;
};

export type LandscapeOutputGroupSummary = {
  category: string;
  itemCount: number;
  linkedItemCount: number;
  extensionTotal: string | null;
  predictedExtensionTotal: string | null;
  bidCountTotal: number | null;
  totalQuantityTotal: string | null;
  lumpSumRatioAvg: number | null;
  predictionTotals: LandscapePredictionTotal[];
  /** See ForecastItemGroupSummary.percentOfTotal. */
  percentOfTotal: number | null;
};

// --- Prediction Filters ---

export type PredictionFilters = {
  stateIds?: string[] | null;
  counties?: string | null;
  districts?: string | null;
  ownerName?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  minBidders?: number | null;
  maxBidders?: number | null;
  bidSizes?: BidAmountRange[] | null;
  /** @deprecated Superseded by `bidSizes`; still honored for forecasts saved before the job-size filter. */
  minAmount?: number | null;
  /** @deprecated Superseded by `bidSizes`; still honored for forecasts saved before the job-size filter. */
  maxAmount?: number | null;
  ranks?: string | null;
  vendorIds?: string[] | null;
  globalVendorIds?: string[] | null;
  vendorName?: string | null;
  bidExternalIds?: string[] | null;
  bidIds?: string[] | null;
  qtyLowerBound?: number | null;
  qtyUpperBound?: number | null;
  lsBandLower?: number | null;
  lsBandUpper?: number | null;
  businessUnitIds?: string[] | null;
  projectTypeIds?: string[] | null;
  estimatorIds?: string[] | null;
  minMarkup?: number | null;
  maxMarkup?: number | null;
  minOverhead?: number | null;
  maxOverhead?: number | null;
  minMargin?: number | null;
  maxMargin?: number | null;
};

// --- Proposal with nested bids ---

export type ProposalWithBids = {
  proposal_id: string;
  letting_id: string;
  contract_id: string | null;
  call_number: string | null;
  description: string | null;
  bidder_count: number | null;
  cost_estimate: string | null;
  districts: string | null;
  letting_date: Date | null;
  owner_name: string;
  county: string | null;
  markup_percent: string | null;
  overhead_percent: string | null;
  margin_percent: string | null;
  bids: {
    bid_id: string;
    vendor_id: string;
    vendor_global_id: string | null;
    vendor_global_name: string | null;
    rank: number | null;
    value: string;
    winner: boolean;
    vendor_name: string | null;
    deviation_percent: string | null;
    winner_value: string | null;
    external_id: string | null;
  }[];
};

// --- Forecast with nested similar bids (for import dialog) ---

export type ForecastWithSimilarBids = {
  forecast_id: string;
  contract_id: string | null;
  description: string | null;
  date: Date | null;
  owner_name: string | null;
  county: string | null;
  bid_count: number;
  markup_percent: string | null;
  overhead_percent: string | null;
  margin_percent: string | null;
  project_type_name: string | null;
  bids: {
    proposal_id: string;
    contract_id: string | null;
    letting_date: string | null;
    rank: number;
    bid_id: string;
    vendor_id: string;
    vendor_name: string | null;
    bid_value: string | null;
    winner: boolean;
    deviation_percent: string | null;
  }[];
};

export type ForecastSimilarBidsFilters = {
  search?: string | null;
  counties?: string | null;
  districts?: string | null;
  excludeForecastId?: string | null;
  ownerId?: string | null;
  businessUnitIds?: string[] | null;
  projectTypeIds?: string[] | null;
  estimatorIds?: string[] | null;
  minMarkup?: number | null;
  maxMarkup?: number | null;
  minOverhead?: number | null;
  maxOverhead?: number | null;
  minMargin?: number | null;
  maxMargin?: number | null;
  limit?: number;
  offset?: number;
};

// --- Stored Similar Bids (enriched) ---

export type StoredSimilarBidsOptions = {
  sort?: string;
  search?: string;
  counties?: string;
  districts?: string[];
  vendorIds?: string[];
  ranks?: number[];
  bidSizes?: BidAmountRange[];
  /** @deprecated Superseded by `bidSizes`; still honored for forecasts saved before the job-size filter. */
  minAmount?: number;
  /** @deprecated Superseded by `bidSizes`; still honored for forecasts saved before the job-size filter. */
  maxAmount?: number;
  businessUnitIds?: string[];
  projectTypeIds?: string[];
  estimatorIds?: string[];
  minMarkup?: number;
  maxMarkup?: number;
  minOverhead?: number;
  maxOverhead?: number;
  minMargin?: number;
  maxMargin?: number;
  limit?: number;
  offset?: number;
};

export type StoredSimilarBid = {
  similar_bid_id: string;
  bid_id: string;
  vendor_id: string;
  vendor_global_id: string | null;
  proposal_id: string;
  owner_id: string;
  letting_id: string;
  call_number: string | null;
  letting_date: string | null;
  contract_id: string | null;
  county: string | null;
  description: string | null;
  bidder_count: number | null;
  vendor_name: string | null;
  bid_value: string | null;
  bid_rank: number | null;
  winner: boolean;
  cost_estimate: string | null;
  owner_name: string;
  districts: string | null;
  winner_value: string | null;
  winner_vendor_name: string | null;
  winner_vendor_id: string | null;
  winner_vendor_global_id: string | null;
  org_bid_value: string | null;
  org_bid_rank: number | null;
  second_place_value: string | null;
  proposal_extension_id: string | null;
  markup_percent: string | null;
  overhead_percent: string | null;
  margin_percent: string | null;
  business_unit_id: string | null;
  business_unit_name: string | null;
  project_type_name: string | null;
  project_types: { id: string; name: string }[];
  estimators: { id: string; name: string }[];
  optimal_margin_percent: string | null;
};

// --- Similar Bid Metrics (aggregate, computed server-side) ---

export type MetricRange = { min: number; max: number } | null;

export type SimilarBidMetrics = {
  jobsBid: number;
  jobsWon: number;
  winRate: number;
  dollarWon: number;
  dollarBid: number;
  hasDollarBid: boolean;
  multiBidderJobsBid: number;
  multiBidderJobsWon: number;
  multiBidderDollarWon: number;
  multiBidderDollarBid: number;
  totalDollarLot: number | null;
  percentLot: number | null;
  mbMarginDollarCaptured: number | null;
  mbAvailableMarginDollar: number | null;
  mbMarginPercent: number | null;
  marginCapturePercent: number | null;
  avgDeviationDollar: number | null;
  avgDeviationPercent: number | null;
  markupRange: MetricRange;
  overheadRange: MetricRange;
  marginRange: MetricRange;
  optimalMarginRange: MetricRange;
};

export type StoredSimilarBidsResult = {
  bids: StoredSimilarBid[];
  metrics: SimilarBidMetrics;
  biddersByProposal: Record<string, ProposalBidderRow[]>;
  competitorOverlay: CompetitorOverlayRow[];
};

// --- Per-proposal bidder rows (all bidders for a matched proposal) ---

export type ProposalBidderRow = {
  bid_id: string;
  vendor_id: string;
  vendor_global_id: string | null;
  proposal_id: string;
  rank: number | null;
  vendor_name: string | null;
  value: string | null;
  extension_total: string | null;
  time_adjustment: string | null;
  evaluation_adjustment: string | null;
  adjusted_total: string | null;
  winner: boolean;
  dollars_from_low: string | null;
  percent_from_low: string | null;
  is_org_vendor: boolean;
};

// --- Competitor overlay (per-vendor patterns across matched set) ---

export type CompetitorMatchup = {
  proposal_id: string;
  letting_id: string;
  contract_id: string | null;
  letting_date: string | null;
  their_rank: number | null;
  their_bid: string | null;
  your_rank: number | null;
  your_bid: string | null;
  winner_name: string | null;
};

export type CompetitorOverlayRow = {
  vendor_id: string;
  vendor_name: string | null;
  times_bid: number;
  times_won: number;
  times_beat_you: number;
  times_you_beat_them: number;
  avg_rank: number | null;
  avg_bid_value: number | null;
  lowest_bid: number | null;
  highest_bid: number | null;
  matchups: CompetitorMatchup[];
};

// --- Margin Analysis ---

export type MarginBand = {
  label: string;
  won: number;
  total: number;
  winRate: number;
};

export type MarginAnalysis = {
  totalJobs: number;
  jobsWon: number;
  avgWinningMargin: number | null;
  avgLosingMargin: number | null;
  avgMaxWinningMargin: number | null;
  avgMarginNeededToWin: number | null;
  minMarginToWin: number | null;
  maxMarginToWin: number | null;
  marginBands: MarginBand[];
  competitors: CompetitorOverlayRow[];
};

// --- Flat Bid (one row per bid, used in flat search dialogs) ---

export type FlatBid = {
  bid_id: string;
  external_id: string | null;
  proposal_id: string;
  letting_id: string;
  letting_date: string | null;
  contract_id: string | null;
  description: string | null;
  owner_state_abbreviation: string | null;
  county: string | null;
  districts: string | null;
  bidder_count: number | null;
  vendor_id: string;
  vendor_global_id: string | null;
  vendor_name: string | null;
  rank: number | null;
  value: string;
  winner: boolean;
  low_bidder_name: string | null;
  low_bid_amount: string | null;
  org_vendor_name: string | null;
  org_bid_amount: string | null;
  org_bid_rank: number | null;
  business_unit_name: string | null;
  project_type_name: string | null;
  estimators: { id: string; name: string }[];
  deviation_percent: string | null;
  markup_percent: string | null;
  overhead_percent: string | null;
  margin_percent: string | null;
};

// --- Post Bid Items by Category ---

export type PostBidVendor = {
  rank: number | null;
  vendorName: string | null;
  bidId: string;
};

export type PostBidVendorTotal = {
  bidId: string;
  extensionTotal: string | null;
};

export type PostBidCategoryRow = {
  category: string;
  itemCount: number;
  vendorTotals: PostBidVendorTotal[];
};

export type PostBidItemDetail = {
  itemId: string;
  lineNumber: number | null;
  itemNumber: string;
  description: string | null;
  quantity: string;
  /** Per-item engineer's estimate (`proposal_items.cost_estimate`); null when
   *  the owner has no engineer's estimate for the line. */
  costEstimate: string | null;
  unit: string | null;
  vendorPrices: PostBidItemVendorPrice[];
};

export type PostBidItemVendorPrice = {
  bidId: string;
  unitPrice: string | null;
  extension: string | null;
};

export type PostBidTotals = {
  vendorTotals: { bidId: string; extensionTotal: string }[];
};

export type PostBidData = {
  vendors: PostBidVendor[];
  categories: PostBidCategoryRow[];
  items: Record<string, PostBidItemDetail[]>;
};

// --- Post Bid Items by Collection ---

export type PostBidCollectionRow = {
  collectionCode: string;
  collectionName: string;
  targetUom: string;
  convertedQuantity: string;
  itemCount: number;
  vendorTotals: PostBidVendorTotal[];
};

export type PostBidCollectionItemDetail = PostBidItemDetail & {
  convertedQuantity: string;
};

export type PostBidCollectionData = {
  vendors: PostBidVendor[];
  collections: PostBidCollectionRow[];
  items: Record<string, PostBidCollectionItemDetail[]>;
};

export type BacklogVendorYearRow = {
  vendorId: string;
  year: number;
  dollarsWon: string;
};

export type BacklogCombinedRow = {
  vendorId: string;
  year: number;
  dollarsWon: string;
  tonsWon: string;
};

export type BacklogBid = {
  proposalId: string;
  contractId: string | null;
  description: string | null;
  county: string | null;
  districts: string | null;
  lettingDate: string;
  value: string;
  completionDate: string | null;
  asphaltTons: string;
};

// --- Accuracy Types ---

export const DATA_PHASE_NONE = 'none' as const;
export const DATA_PHASE_APPARENT = 'apparent' as const;
export const DATA_PHASE_BID_TABULATIONS = 'bid_tabulations' as const;

export type AccuracyDataPhase =
  | typeof DATA_PHASE_NONE
  | typeof DATA_PHASE_APPARENT
  | typeof DATA_PHASE_BID_TABULATIONS;

export const POST_BID_MODE_ACTUALS = 'actuals' as const;
export const POST_BID_MODE_PREDICT = 'predict' as const;

export type PostBidMode =
  | typeof POST_BID_MODE_ACTUALS
  | typeof POST_BID_MODE_PREDICT;

export type AccuracyBidderPresence = {
  predicted: Array<{ vendorId: string; vendorName: string }>;
  actual: Array<{ vendorId: string; vendorName: string; rank: number }>;
  confirmed: Array<{ vendorId: string; vendorName: string }>;
  surprise: Array<{ vendorId: string; vendorName: string; rank: number }>;
  noShow: Array<{ vendorId: string; vendorName: string }>;
};

export type AccuracyCompetitorDelta = {
  vendorId: string;
  vendorName: string;
  predictedTotal: number;
  actualTotal: number;
  signedDelta: number;
  percentDelta: number;
  predictedRank: number;
  actualRank: number;
};

export type AccuracyItemVariance = {
  vendorId: string;
  vendorName: string;
  lineNumber: number;
  itemNumber: string;
  /**
   * Catalog item uuid (`items.id`) resolved through the bid line's
   * proposal item — used to deep-link to the item detail page.
   * Null when the linked catalog item is soft-deleted.
   */
  itemId: string | null;
  description: string;
  category: string;
  quantity: number;
  unit: string;
  predictedUnitPrice: number;
  actualUnitPrice: number;
  predictedExtension: number;
  actualExtension: number;
  extensionDelta: number;
  absExtensionDelta: number;
};

export type AccuracyCategoryRollup = {
  category: string;
  vendors: Array<{
    vendorId: string;
    vendorName: string;
    predictedTotal: number;
    actualTotal: number;
    signedDelta: number;
    percentDelta: number;
  }>;
};

export type AccuracyMyPrice = {
  myTotal: number;
  actualLowBid: number;
  signedDelta: number;
  percentDelta: number;
};

export type AccuracyMyPriceItem = {
  lineNumber: number;
  itemNumber: string;
  myUnitPrice: number;
  myExtension: number;
};

export type AccuracySummary = {
  forecastId: string;
  proposalId: string | null;
  contractId: string;
  description: string;
  ownerName: string;
  dataPhase: AccuracyDataPhase;
  rankAccuracy: {
    predictedWinner: string | null;
    actualWinner: string | null;
    match: boolean;
  };
  bidderPresence: AccuracyBidderPresence;
  competitorDeltas: AccuracyCompetitorDelta[];
  myPrice: AccuracyMyPrice | null;
  myPriceItems: AccuracyMyPriceItem[];
};

export type ForecastDashboardForecast = {
  forecastId: string;
  contractId: string | null;
  description: string | null;
  date: string | null;
  myTotal: number;
  actualLow: number | null;
  marginPct: number | null;
  won: boolean | null;
  ranksMatched: number;
  ranksTotal: number;
};

export type ForecastDashboardStats = {
  totalForecasts: number;
  forecastsWithResults: number;
  totalBidVolume: number;
  wins: number;
  winRateDenominator: number;
  winRate: number | null;
  avgPredictionGapPct: number | null;
  rankAccuracyPct: number | null;
  forecasts: ForecastDashboardForecast[];
};
